import { Head, Link, router, usePage } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'

import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {AlertCircle, CreditCard, DollarSign, Plus, TrendingUp} from 'lucide-react'

import Form from './form'
import { DeviceType } from './types'

interface CommunityOption {
  id: string
  name: string
}

interface IndexProps {
  devices: DeviceType[]
  communities: CommunityOption[]
}

export default function Index({ devices, communities }: IndexProps) {
  const { flash } = usePage()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingDevice, setEditingDevice] = useState<DeviceType | null>(null)
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window === 'undefined') return ''
    return new URLSearchParams(window.location.search).get('q') ?? ''
  })
  const [statusFilter, setStatusFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all'
    return new URLSearchParams(window.location.search).get('status') ?? 'all'
  })
  const [registeredFilter, setRegisteredFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all'
    return new URLSearchParams(window.location.search).get('registered') ?? 'all'
  })
  const [communityFilter, setCommunityFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all'
    return new URLSearchParams(window.location.search).get('community_id') ?? 'all'
  })

  const emptyDevice = useMemo(
    () => ({
      id: '',
      name: '',
      code: '',
      model: '',
      community_id: '',
      identifier: '',
      status: 'active',
      registered: false,
    }),
    []
  )

  const isEditing = Boolean(editingDevice)
  const formDevice = editingDevice ?? emptyDevice
  const formAction = isEditing
    ? `/system/devices/${formDevice.id}`
    : '/system/devices'
  const formMethod = isEditing ? 'patch' : 'post'
  const formTitle = isEditing ? 'Editer le terminal' : 'Ajouter un nouveau terminal'
  const formSubmitText = isEditing ? 'Enregister' : 'Enregister'

  const openCreateDialog = () => {
    setEditingDevice(null)
    setDialogOpen(true)
  }


  useEffect(() => {
    const timeout = setTimeout(() => {
      router.get(
        '/system/devices',
        {
          q: searchTerm.trim() || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          registered: registeredFilter === 'all' ? undefined : registeredFilter,
          community_id: communityFilter === 'all' ? undefined : communityFilter,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: ['devices', 'communities'],
        }
      )
    }, 300)

    return () => clearTimeout(timeout)
  }, [searchTerm, statusFilter, registeredFilter, communityFilter])

  useEffect(() => {
    if (flash?.notice) {
      setDialogOpen(false)
      setEditingDevice(null)
    }
  }, [flash?.notice])

  return (
    <>
      <Head title="Devices" />
      <div className="p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">POS</h2>
            <p className="text-muted-foreground">
              Gestion des terminaux de collecte
            </p>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Ajouter un terminal
          </Button>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active POS Devices</p>
                <p className="text-2xl font-bold text-foreground">0</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-success/10 p-2">
                <DollarSign className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Today's POS Collections</p>
                <p className="text-2xl font-bold text-foreground">0 FCFA</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-accent/10 p-2">
                <TrendingUp className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Transactions</p>
                <p className="text-2xl font-bold text-foreground">0</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-warning/10 p-2">
                <AlertCircle className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Low Battery Devices</p>
                <p className="text-2xl font-bold text-foreground">1</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-[1fr_180px_200px_200px]">
          <Input
            placeholder="Search by device name"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={registeredFilter} onValueChange={setRegisteredFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Registered" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All devices</SelectItem>
              <SelectItem value="true">Registered</SelectItem>
              <SelectItem value="false">Not registered</SelectItem>
            </SelectContent>
          </Select>
          <Select value={communityFilter} onValueChange={setCommunityFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Community" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All communities</SelectItem>
              {communities.map((community) => (
                <SelectItem key={community.id} value={community.id}>
                  {community.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">

          <div className="space-y-3">
            {devices.map((device) => (
                <Link key={device.id} href={`/system/devices/${device.id}`}>
                  <div className="rounded-lg border border-border p-4 transition-colors hover:bg-secondary/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="text-lg font-semibold text-foreground">{device.name}</p>
                          <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                  device.status === "Active" ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"
                              }`}
                          >
                        {device.status}
                      </span>
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary">
                        {device.code}
                      </span>
                        </div>

                        <p className="mt-1 text-sm text-muted-foreground">
                          {/*Agent: {device.agentName} ({device.agentId})*/}
                        </p>

                        <div className="mt-3 grid gap-4 md:grid-cols-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Today's Transactions</p>
                            <p className="mt-1 text-sm font-semibold text-foreground">0</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Today's Amount</p>
                            <p className="mt-1 text-sm font-semibold text-success">
                              0 FCFA
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Battery Level</p>
                            <p
                                className={`mt-1 text-sm font-semibold ${10 < 50 ? "text-warning" : "text-foreground"}`}
                            >
                              10%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Last Transaction</p>
                            <p className="mt-1 text-sm text-foreground">0</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{formTitle}</DialogTitle>
          </DialogHeader>
          <Form
            key={`${isEditing ? formDevice.id : 'new'}`}
            device={formDevice}
            communities={communities}
            action={formAction}
            method={formMethod}
            submitText={formSubmitText}
            onSuccess={() => {
              setDialogOpen(false)
              setEditingDevice(null)
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
